import * as React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton, SkeletonCard } from '../primitives/Skeleton';
import { NeonLoadingRing } from './NeonLoadingRing';

/**
 * v7 LoadingState - Loading skeleton displays
 *
 * Per UX Spec v7.1:
 * - Skeleton loading patterns
 * - Content-aware placeholders
 * - Shimmer animation
 * - No spinners (use skeletons instead)
 */

export interface LoadingStateProps {
  /** Loading variant */
  variant?: 'default' | 'page' | 'card' | 'list' | 'grid' | 'inline';
  /** Number of skeleton items for list/grid */
  count?: number;
  /** Whether to show a loading message */
  message?: string;
  /** Additional class names */
  className?: string;
  /** Children for custom skeleton content */
  children?: React.ReactNode;
}

export const LoadingState = React.forwardRef<HTMLDivElement, LoadingStateProps>(
  ({ variant = 'default', count = 3, message, className, children }, ref) => {
    if (children) {
      return (
        <div ref={ref} className={cn('animate-pulse', className)}>
          {message && (
            <p className="font-v7-body text-sm text-v7-text-secondary mb-4">
              {message}
            </p>
          )}
          {children}
        </div>
      );
    }

    switch (variant) {
      case 'page':
        return (
          <div ref={ref} className={cn('space-y-6', className)}>
            {/* Header skeleton */}
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            {/* Content skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: count }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        );

      case 'card':
        return (
          <div ref={ref} className={className}>
            <SkeletonCard />
          </div>
        );

      case 'list':
        return (
          <div ref={ref} className={cn('space-y-4', className)}>
            {message && (
              <p className="font-v7-body text-sm text-v7-text-secondary">
                {message}
              </p>
            )}
            {Array.from({ length: count }).map((_, i) => (
              <LoadingListItem key={i} />
            ))}
          </div>
        );

      case 'grid':
        return (
          <div
            ref={ref}
            className={cn(
              'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
              className
            )}
          >
            {Array.from({ length: count }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        );

      case 'inline':
        return (
          <div
            ref={ref}
            className={cn('flex items-center gap-3', className)}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <LoadingSpinner />
            {message && (
              <span className="font-v7-body text-sm text-v7-text-secondary">
                {message}
              </span>
            )}
            {!message && <span className="sr-only">Loading</span>}
          </div>
        );

      default:
        return (
          <div
            ref={ref}
            className={cn(
              'flex flex-col items-center justify-center py-12',
              className
            )}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <NeonLoadingRing className="h-12 w-12" />
            {message && (
              <p className="font-v7-body text-sm text-v7-text-secondary mt-4">
                {message}
              </p>
            )}
            {!message && <span className="sr-only">Loading</span>}
          </div>
        );
    }
  }
);

LoadingState.displayName = 'LoadingState';

// List item skeleton
function LoadingListItem() {
  return (
    <div className="flex items-center gap-4 p-4 bg-v7-bg-surface rounded-v7-lg">
      {/* Avatar/thumbnail */}
      <Skeleton className="w-12 h-12 rounded-v7-md flex-shrink-0" />
      {/* Content */}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      {/* Action */}
      <Skeleton className="h-8 w-20 rounded-v7-md" />
    </div>
  );
}

// Loading spinner (used sparingly, skeletons preferred)
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function LoadingSpinner({ size = 'md', className }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  return (
    <div className={cn('relative', sizeClasses[size], className)}>
      <svg
        className="animate-spin"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="3"
          className="opacity-20"
        />
        <path
          d="M12 2a10 10 0 0 1 10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          className="text-v7-orange"
        />
      </svg>
    </div>
  );
}

// Full page loading overlay
export interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  visible?: boolean;
  /** Loading message */
  message?: string;
  /** Additional class names */
  className?: string;
}

export function LoadingOverlay({
  visible = true,
  message,
  className,
}: LoadingOverlayProps) {
  if (!visible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50',
        'bg-v7-bg-primary/80',
        'backdrop-blur-sm',
        'flex flex-col items-center justify-center',
        className
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <NeonLoadingRing className="h-12 w-12" />
      {message && (
        <p className="font-v7-body text-sm text-v7-text-secondary mt-4">
          {message}
        </p>
      )}
      {!message && <span className="sr-only">Loading</span>}
    </div>
  );
}

export const LoadingStateCards = React.forwardRef<
  HTMLDivElement,
  Omit<LoadingStateProps, 'variant'>
>((props, ref) => <LoadingState ref={ref} variant="grid" {...props} />);
LoadingStateCards.displayName = 'LoadingStateCards';
